




































































import {
  Vue,
  Component,
  Watch,
} from "@/shared/vuepack";
import {
  Validator,
  ErrorBag
} from "vee-validate";
import {
  FundingOption,
  EligibilityStatus
} from "@/shared/types";
import Button from "@/shared/components/ui/Button.vue";
import ContributionsProgressBar from "@/employee/components/shared/ContributionsProgressBar.vue";
import moment from "moment";
import _ from "lodash";
import HelpIcon from "@/shared/components/ui/HelpIcon.vue";
import GenericLoadingSpinner from "@/shared/components/ui/GenericLoadingSpinner.vue";
import { useUserStore } from "@/shared/store/user";
import { useHsaLimitStore } from "@/shared/store/hsalimit";
import { useHsaTotalStore } from "@/shared/store/hsaTotal";
import { useHsaStore } from "@/shared/store/hsa";
import { useBankAccountStore } from "@/employee/store/bankAccount";
import { formatCurrency } from "@/shared/currency";

@Component({
  components: {
    Button,
    ContributionsProgressBar,
    HelpIcon,
    GenericLoadingSpinner
  }
})
export default class RecurringPayrollContribution extends Vue {

  bankAccountStore = useBankAccountStore();
  hsaLimitStore = useHsaLimitStore();
  hsaTotalStore = useHsaTotalStore();
  hsaStore = useHsaStore();
  userStore = useUserStore();

  annualEmployeeContributionFormBackingField = 0;
  nextRecurringContributionFormBackingField = 0;

  $validator: Validator;
  errors: ErrorBag;
  lastTouched = "";
  isSubmitting = false;
  hideEarlyDepositDate = false;
  formatCurrency = formatCurrency;

  mounted () {
    this.hsaLimitStore.getHsaAccountLimits();
    this.hsaTotalStore.getUserPayrollSummary();
    this.bankAccountStore.getBankAccountStatus();
    this.pendingAmountChanged();
    if (this.hsaTotalStore.isLoaded) {
      this.updateFromStore();
    }
  }

  get hsaLimit () {
    return this.hsaLimitStore.getHsaLimitByYear(moment().year());
  }

  get isEligible () {
    return this.hsaTotalStore.eligibilityStatus === EligibilityStatus.Eligible;
  }

  get showInput () {
    if (this.individualMode || this.isErCalculateBendPullFundingOption) {
      return false;
    }

    return this.hsaTotalStore.eligibilityStatus === EligibilityStatus.Eligible ||
        this.hsaTotalStore.eligibilityStatus === EligibilityStatus.NoAccount;
  }

  get individualMode () {
    return this.hsaTotalStore.alwaysUseInitialAnnualContributionForTotalEmployeePayrollContributions;
  }

  get showInEligibleMessage () {
    return this.hsaTotalStore.eligibilityStatus === EligibilityStatus.Ineligible && !this.individualMode;
  }

  get isErCalculateBendPullFundingOption () {
    return this.userStore.employeeFundingOption === FundingOption.ErCalculateBendPull;
  }

  get earlyDepositCompleteDateDisplay () {
    return !this.hideEarlyDepositDate && this.hsaTotalStore.earlyDepositCompleteDate &&
        this.hsaTotalStore.earlyDepositCompleteDate.format("MMM DD");
  }

  onSubmit () {
    this.$validator.validateAll().then(result => {
      if (!result) {
        return;
      }
      if (this.isSubmitting) {
        return;
      }
      this.isSubmitting = true;
      this.hsaTotalStore.updateRecurringContribution(this.nextRecurringContributionForm)
          .then(() => {
            this.isSubmitting = false;
            this.hideEarlyDepositDate = false;
          })
          .catch(() => {
            this.isSubmitting = false;
          });
    });
  }

  @Watch("contributionState")
  onContributionStateChange () {
    this.updateFromStore();
  }

  @Watch("hsaTotalStore.nextEligiblePayPeriodAmount")
  onNextEligiblePayPeriodAmountChange () {
    this.updateFromStore();
  }

  @Watch("hsaTotalStore.initialAnnualPreTaxContribution")
  onInitialAnnualContributionChange () {
    this.updateFromStore();
  }

  @Watch("hsaTotalStore.isLoaded")
  onLoaded () {
    if (this.hsaTotalStore.isLoaded) {
      this.updateFromStore();
    }
  }

  updateFromStore () {
    this.annualEmployeeContributionFormBackingField = this.hsaTotalStore.initialAnnualPreTaxContribution;
    this.nextRecurringContributionFormBackingField = this.hsaTotalStore.nextEligiblePayPeriodAmount;
  }

  get isSubmitDisabled () {

    if (this.bankAccountStore.isBankAccountClosed) {
      return true;
    }

    return this.isSubmitting ||
        this.errors.any() ||
        !(this.hsaTotalStore.isLoaded && this.isEligible);
  }

  get validateAnnualContribution () {
    return {
      required: true,
      decimal: 2,
      mimimumRecurringAnnualContribution: this.hsaTotalStore.mimimumRecurringAnnualContribution,
      max_value_usd: this.hsaTotalStore.maximumRecurringAnnualContribution
    };
  }

  get validatePerPayPeriod () {
    return {
      required: true,
      decimal: 2,
      min_value_usd: 0,
      max_value_usd: this.hsaTotalStore.maximumRemainingContribution
    };
  }

  get annualEmployeeContributionForm () {
    return this.annualEmployeeContributionFormBackingField;
  }

  set annualEmployeeContributionForm (newValue) {
    this.lastTouched = "Annual contribution";
    this.hideEarlyDepositDate = true;
    if (isNaN(newValue) || isNaN(parseFloat(newValue))) {
      newValue = 0;
    } else if (typeof (newValue) === "string") {
      newValue = parseFloat(newValue);
    }
    this.annualEmployeeContributionFormBackingField = newValue;
    const proposedValue = this.calculatePerPayPeriodAmount(newValue);
    this.nextRecurringContributionFormBackingField = proposedValue;
  }

  get nextRecurringContributionForm () {
    return this.nextRecurringContributionFormBackingField || 0;
  }

  set nextRecurringContributionForm (newValue) {
    this.lastTouched = "Per pay period contribution";
    this.hideEarlyDepositDate = true;
    this.nextRecurringContributionFormBackingField = newValue;
    if (isNaN(newValue)) {
      return;
    }
    const proposedAnnualAmount = (newValue * this.hsaTotalStore.remainingPayPeriods) +
        this.hsaTotalStore.mimimumRecurringAnnualContribution;
    this.annualEmployeeContributionFormBackingField = _.floor(proposedAnnualAmount, 2);
  }

  calculatePerPayPeriodAmount (newAnnualAmount) {
    const upcomingAmountProposed = _.floor(newAnnualAmount - this.hsaTotalStore.mimimumRecurringAnnualContribution, 2);
    const upcomingAmount = upcomingAmountProposed < 0.01 ? 0 : upcomingAmountProposed;

    if (this.hsaTotalStore.remainingPayPeriods < 2 || upcomingAmount <= 0.01) {
      // not enough pay periods or not enough money to distribute
      return upcomingAmount;
    }

    const perPeriodRaw = upcomingAmount / this.hsaTotalStore.remainingPayPeriods;
    return _.floor(perPeriodRaw, 2);
  }

  @Watch("annualEmployeeContributionForm")
  pendingAmountChanged () {
    this.$emit("pendingAmount", this.annualEmployeeContributionForm);
  }

  get revisedAmount (): number {
    return this.nextRecurringContributionForm + this.hsaTotalStore.oneTimeLift.amount;
  }

  get effectivePayDate (): string {
    return moment(this.hsaTotalStore.oneTimeLift.payDate).format("MMMM Do YYYY");
  }
}
